import React, { useEffect } from 'react';
import {
  DigitalCase,
  DigitalSection,
  FaerbungReviewCategories,
  FaerbungReviewMeta,
} from '../../redux/types';
import { useAppDispatch } from '../reduxHooks';
import {
  addUpdateFaerbungReviewMetas,
  deleteFaerbungReviewMeta,
} from '../../redux/actions/faerbungReviewAction';

const DEFAULT_FAERBUNG_CATEGORIES = [
  {
    category: FaerbungReviewCategories.sensitivity,
    score: 5,
  },
  {
    category: FaerbungReviewCategories.specificity,
    score: 5,
  },
];

/**
   * This hook effect creates or deletes
   * FaerbungReviewMeta entries in the database and redux,
   * based on DigitalSection addition and deletion
   */
const useCreateDeleteReviewMeta = (
  sections: DigitalSection[],
  surveyId: number,
  allCases: DigitalCase[],
  faerbungReviewMetas?: FaerbungReviewMeta[],
): void => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!faerbungReviewMetas) {
      /**
       * FaerbungReviewMetas are `undefined` as long as the fetch call is not completed.
       * In case of the fetch call is completed, but there are no meta exists in the db,
       * the faerbungReviewMeta in redux state will be an empty array
       */
      return;
    }
    // auto-create faerbungReviewMeta entries based sections (creation, deletion)
    const getMaxScore = (sectionId: number, score: number) => {
      const allSecCases = allCases.filter((c) => c.digitalSectionId === sectionId);
      return allSecCases.length * (DEFAULT_FAERBUNG_CATEGORIES.reduce((a, c) => (a + c.score), 0));
    };
    const getNonInitiatedSections = (
      s: DigitalSection[], frm: FaerbungReviewMeta[],
    ): [number[], FaerbungReviewMeta[]] => {
      const existentialCrisis = s.reduce((acc, cur) => {
        acc[cur.id] = 0;
        return acc;
      }, {} as Record<number, number>);
      const deletionMeta: FaerbungReviewMeta[] = [];
      frm.forEach((meta) => {
        if (existentialCrisis[meta.digitalSectionId] === 0) {
          existentialCrisis[meta.digitalSectionId] = 1;
        } else if (existentialCrisis[meta.digitalSectionId] === undefined) {
          deletionMeta.push(meta);
        }
      });
      const creationMeta: number[] = Object.entries(existentialCrisis)
        .filter(([k, v]) => v === 0).map(([k, v]) => Number(k));
      return [creationMeta, deletionMeta];
    };
    const [creationMeta, deletionMeta] = getNonInitiatedSections(sections, faerbungReviewMetas);
    if (creationMeta && creationMeta.length) {
      const metaObjs = creationMeta.map((sId): Omit<FaerbungReviewMeta, 'id'> => ({
        digitalSectionId: sId,
        digitalSurveyId: surveyId,
        categories: DEFAULT_FAERBUNG_CATEGORIES,
        maxScore: getMaxScore(sId, 5),
        passingScore: 0,
      }));
      dispatch(addUpdateFaerbungReviewMetas(metaObjs));
    }
    if (deletionMeta && deletionMeta.length) {
      deletionMeta.forEach((m) => dispatch(deleteFaerbungReviewMeta(m.id)));
    }
  }, [
    faerbungReviewMetas,
    surveyId,
    sections,
    allCases,
    dispatch,
  ]);
};

export default useCreateDeleteReviewMeta;
